import logger from '~/services/logger';
import type { Feed, UIComponent } from '~/services/layout/index.ts';
import { GroupComponents } from './group-components';
import { isEmptyGroup } from './helpers';
import { MaybeEmptyGroup } from './maybe-empty-group';
import { getComponentMarginsOnMaxWidth } from '~/utils/styles';
import { getPluginConfig } from '~/utils/get-plugin-configuration';
import manifest from '../config/manifest';
import { isEnableDataRefreshing } from '~/services/ui-components';
import type { StorageFeedsData } from '~/components/ui-component';
import type { UseIsLoggedInReturnType } from '~/hooks/use-is-logged-in';

export default function Group({
  uiComponent,
  feed,
  isLoading,
  isClient,
  isClientStorage,
  storageFeedsData,
  isLoggedInData,
}: {
  uiComponent: UIComponent;
  feed?: Feed;
  isLoading?: boolean;
  isClient?: boolean;
  isClientStorage?: boolean;
  storageFeedsData: StorageFeedsData;
  isLoggedInData: UseIsLoggedInReturnType;
}): JSX.Element {
  try {
    const isGroupComponent: boolean = uiComponent.component_type === 'group-qb';

    const isEmpty: boolean = feed?.entry?.length === 0 || !feed?.entry;

    const hideComponentWhenEmpty: boolean =
      uiComponent?.rules?.hide_component_if_data_is_empty;

    const hasEmptyGroupComponent: boolean =
      uiComponent?.ui_components?.some(isEmptyGroup);

    if (
      !isLoading &&
      (!isGroupComponent ||
        (isEmpty && hideComponentWhenEmpty && !hasEmptyGroupComponent))
    ) {
      return <></>;
    }

    const emptyGroupComponent = uiComponent?.ui_components?.find(isEmptyGroup);

    const groupComponents = uiComponent?.ui_components?.filter(
      (c) => !isEmptyGroup(c)
    );

    const uiComponentId = uiComponent.id;

    const { styles } = getPluginConfig<typeof manifest>(manifest, uiComponent);

    const isGroupComponentDataRefreshing: boolean =
      isEnableDataRefreshing(uiComponent);

    return (
      <div
        className={`group-${uiComponentId} mb-group-b mt-group-t bg-group pb-group-b pl-group-l pr-group-r pt-group-t ${getComponentMarginsOnMaxWidth(
          { styles }
        )} component-maximum-width`}
      >
        <MaybeEmptyGroup uiComponent={emptyGroupComponent} feed={feed} />

        <GroupComponents
          uiComponents={groupComponents}
          feed={feed}
          repeatPatternType={uiComponent?.rules?.repeat_pattern_type}
          repeatNumOfLastComponents={
            uiComponent?.rules?.repeat_num_of_components
          }
          isClient={isClient}
          isLoading={isLoading}
          isClientStorage={isClientStorage}
          isGroupComponentDataRefreshing={isGroupComponentDataRefreshing}
          storageFeedsData={storageFeedsData}
          isLoggedInData={isLoggedInData}
        />
      </div>
    );
  } catch (error: any) {
    logger.info(`Group: ${error.message}`);
    return <></>;
  }
}
